import { ActionEnum } from '../enum/action.enum';
import { action, toJS } from 'mobx';
import { isString } from 'lodash';
import { ActionFunction } from '../interface/action/action-function.type';
import { DesignObjectModel } from '../model/design-object.model';
import { manageEditor } from '../util/action/type/manage-editor-action.util';
import { getDesignDraftId } from '../util/page/get-design-draft-id.util';
import { isPageModel } from '../util/design-object/is-page-model.util';
import { IDeletePageDraftPayload } from './page.action';
import { newDynamicPage } from '../util/action/type/new-dynamic-page-action.util';
import { saveDesignObjectDraft } from '../util/action/type/save-design-object-draft-action.util';
import { IDesignObjectData } from '../interface/design-object-data.interface';
import { getDesignObjectMetadata } from '../util/action/type/get-design-object-metadata-action.util';
import { noop } from '../util/action/type/noop-action.util';
import { createDesignObject } from '../util/design-object/create-design-object.util';
import { redirectPage } from '../util/action/type/redirect-page.util';
import { ADMIN_PREVIEW_KEYWORD } from '../constants';
import { formPreviewPath } from '../util/url/form-preview-path.util';
import { isCustomListModel } from '../util/design-object/is-custom-list-model.util';
import { createLinkedDataFromData } from '../util/linked-data/create-linked-data-from-data';

export const resetActiveDesignObject$: ActionFunction = (action$, {state}) => {
  return action$
    .type(ActionEnum.RESET_ACTIVE_DESIGN_OBJECT)
    .end(action(() => {
      state.activeDesign.lastDataSyncState = null;
    }));
};

export const removeActiveDesignObject$: ActionFunction = (action$, {state}) => {
  return action$
    .type(ActionEnum.REMOVE_ACTIVE_DESIGN_OBJECT)
    .end(action(() => {
      state.activeDesign.setDesignData(null, state.configuration.inAdmin);
      state.activeDesign.lastDataSyncState = null;
    }));
};

export const previewDesignObject$: ActionFunction = (action$, {state, history}) => {
  return action$
    .type(ActionEnum.PREVIEW_DESIGN_OBJECT)
    .map(() => {
      return redirectPage(formPreviewPath(history.location.pathname, state.previewMode));
    });
};

export const closePreviewDesignObject$: ActionFunction = (action$, {history}) => {
  return action$
    .type(ActionEnum.CLOSE_PREVIEW_DESIGN_OBJECT)
    .map(action(() => {
      return redirectPage(history.location.pathname.replace(new RegExp(`#${ADMIN_PREVIEW_KEYWORD}[^\\s]*$`, 'i'), ''));
    }));
};

export const saveDesignObjectDraft$: ActionFunction<DesignObjectModel> = (action$, {logger, state}) => {
  return action$
    .type(ActionEnum.SAVE_DESIGN_OBJECT_DRAFT)
    .map(action((design: DesignObjectModel) => {
      try {
        const pageData = toJS(design);
        localStorage.setItem(getDesignDraftId(design), JSON.stringify({
          designData: pageData,
          siteVersion: state.configuration.siteVersion
        }));
        design.isDraft = true;
      } catch (e) {
        logger.warn('Can\'t save design draft!');
      }
      return manageEditor();
    }));
};

export const deleteDesignObjectDraft$: ActionFunction<IDeletePageDraftPayload> = (action$, {logger, state}) => {
  return action$
    .type(ActionEnum.DELETE_DESIGN_DRAFT)
    .map(action((payload: IDeletePageDraftPayload) => {
      try {
        localStorage.removeItem(getDesignDraftId(payload.design));
      } catch (e) {
        logger.warn('Can\'t delete page draft!');
      }
      if (!isString(payload.design)) {
        if (payload.shouldRevert && state.activeDesign.data === payload.design) {
          if (state.activeDesign.lastDataSyncState) {
            state.activeDesign.setDesignData(state.activeDesign.lastDataSyncState, state.configuration.inAdmin);
          } else if (isPageModel(state.activeDesign.data)) {
            state.activeDesign.data.isNew = false;
            return newDynamicPage();
          }
        } else {
          payload.design.isDraft = false;
        }
      }
      return manageEditor();
    }));
};

export const loadNewDesignObjectDraft$: ActionFunction = (action$, {state, logger}) => {
  return action$
    .type(ActionEnum.LOAD_NEW_DESIGN_OBJECT_DRAFT)
    .map(action(() => {
      let designDraft;
      try {
        designDraft = localStorage.getItem(getDesignDraftId());
      } catch (e) {
        logger.warn('Can\'t load page from draft');
      }
      if (designDraft) {
        const draftData = JSON.parse(designDraft);
        if (draftData.siteVersion !== state.configuration.siteVersion) {
          localStorage.removeItem(getDesignDraftId());
        } else {
          const designData = draftData.designData;
          state.activeDesign.setDesignData(designData, state.configuration.inAdmin, true);
        }
      }
      return manageEditor();
    }));
};

export const loadDesignObjectDraft$: ActionFunction<DesignObjectModel> = (action$, {state, logger}) => {
  return action$
    .type(ActionEnum.LOAD_DESIGN_OBJECT_DRAFT)
    .map(action((design: DesignObjectModel) => {
      let pageDraft;
      try {
        pageDraft = localStorage.getItem(getDesignDraftId(design));
      } catch (e) {
        logger.warn('Can\'t load design from draft');
      }
      if (pageDraft) {
        let linkedData = [];
        if (state.activeDesign.data) {
          linkedData = state.activeDesign.data.linkedData;
        }
        pageDraft = JSON.parse(pageDraft);
        if (pageDraft.siteVersion !== state.configuration.siteVersion) {
          localStorage.removeItem(getDesignDraftId(design));
        } else {
          const pageData = pageDraft.designData;
          if (!pageData) {
            return noop();
          }
          state.activeDesign.setDesignData(pageData, state.configuration.inAdmin, true);
          pageData.linkedData = linkedData.concat(pageData._linkedData ? pageData._linkedData.filter(data => linkedData.every(oldData => oldData.id !== data.id)).map((data => createLinkedDataFromData(data, state.configuration.inAdmin))) : []);
        }
      }
      return manageEditor();
    }));
};

const notSettingKeys = [ 'linkedData', 'dateUpdated', 'blocks' ];

export const updateActiveDesignObjectSettings$: ActionFunction<IDesignObjectData> = (action$, {state}) => {
  return action$
    .type(ActionEnum.UPDATE_ACTIVE_DESIGN_OBJECT_SETTINGS)
    .map((payload: IDesignObjectData) => {
      if (payload.type !== state.activeDesign.data.type) {
        state.activeDesign.setDesignData(payload, state.configuration.inAdmin);
      } else {
        const newDesign = createDesignObject(payload, state.configuration.inAdmin);
        const keys = Object.keys(newDesign);
        keys.forEach(action((key: string) => {
          if (notSettingKeys.indexOf(key) === -1) {
            state.activeDesign.data[ key ] = newDesign[ key ];
          }
        }));
      }
      return [ saveDesignObjectDraft(state.activeDesign.data), getDesignObjectMetadata(state.activeDesign.data.type, isCustomListModel(state.activeDesign.data) ? state.activeDesign.data.customListDefinitionId : undefined) ];
    });
};

export default {
  actions: [
    resetActiveDesignObject$,
    removeActiveDesignObject$,
    previewDesignObject$,
    closePreviewDesignObject$,
    loadNewDesignObjectDraft$,
    loadDesignObjectDraft$,
    saveDesignObjectDraft$,
    deleteDesignObjectDraft$,
    updateActiveDesignObjectSettings$
  ]
};
